import axios, { AxiosInstance } from 'axios';
import {
  AccountApi,
  AgreementsApi,
  AuthApi,
  BankingInformationApi,
  ContractsApi,
  ContractTypesApi,
  DepartmentsApi,
  DocumentTypesApi,
  DepartmentsEmployeesApi,
  DepartmentsEmployeesCalendarEntriesApi,
  SegmentApi,
  EmployeeDocumentApi,
  EmployeesApi,
  ExperienceLevelApi,
  WorkPositionsApi,
  BuildingsApi,
  RoomsApi,
  StatusApi,
  EmployeeNoteApi,
  EmployeePaymentsApi,
  SeasonsApi,
  UsersApi,
  TokenApi,
  PreContractsApi,
  NotificationsApi,
  TenantConfigApi,
  RewardsApi,
  CommunicationsApi,
  JobAdvertisementsApi,
  LicenceApi,
  AdditionalInformationApi,
  ShiftTemplateApi,
  ShiftApi,
  CalendarEntryShiftTemplateApi,
  PaymentMethodsApi,
  SectionsApi,
  TurnoverItemsApi,
  SimpleDepositsApi,
  TenantBankingInformationApi,
  DailyNotesApi,
  ShiftNotesApi,
  EmployeeDayNotesApi,
  StatusChangeApi,
  PublicDocumentsApi,
  ResumeApi,
  DailyTicketsApi,
  SafetyAreasApi,
  SafetyItemsApi,
  PipelinesApi,
  ApplicationsApi,
  CandidateCommentsApi,
  InterviewsApi,
  EmailTemplatesApi,
  OfferLettersApi,
  CourseTypesApi,
  TrainingCoursesApi,
  TrainingRequirementsApi,
  EmployeeCertificationsApi,
  TrainingComplianceApi,
  DashboardApi,
  CandidatesApi,
  TimeEntriesApi,
  TimeEntryApprovalsApi,
  TimeEntryReportsApi,
  PublicHolidaysApi,
} from './generated';

const origin = window.location.origin;
const baseURL = process.env.REACT_APP_BASE_URL ?? origin;

export const apiURL =
  process.env.REACT_APP_API_URL ??
  (baseURL ? `${baseURL}/api/v1` : '/api/v1');

export const realAxios = axios.create({
  baseURL: apiURL,
});

realAxios.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

realAxios.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response.status !== 401) {
      const response = error.response;
      console.error(`Error ${response.status}: `, response.data);
      return Promise.reject(error);
    }
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      return Promise.reject(error);
    }
    const { config } = error.response;

    return refreshTokens(refreshToken)
      .then(async (response) => {
        const { access_token: accessToken, refresh_token: refreshToken } =
          response.data;
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', refreshToken);

        // redo call with updated access token
        config.headers.Authorization = `Bearer ${accessToken}`;
        return axios(config).catch(async (error) => {
          console.error(error);
          return Promise.reject(error);
        });
      })
      .catch(async (e) => {
        if (e.status === 401) {
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
        }
        return Promise.reject(error);
      });
  },
);

async function refreshTokens(refreshToken: string) {
  // use a fresh instance of axios to prevent an infinite loop in the interceptors
  return axios.get('auth/refresh', {
    baseURL: apiURL,
    headers: {
      Authorization: `Bearer ${refreshToken}`,
    },
  });
}

const params: [undefined, string | undefined, AxiosInstance] = [
  undefined,
  baseURL,
  realAxios,
];

export const departmentsApi = new DepartmentsApi(...params);
export const segmentApi = new SegmentApi(...params);
export const contractTypesApi = new ContractTypesApi(...params);
export const authApi = new AuthApi(...params);
export const workPositionsApi = new WorkPositionsApi(...params);
export const experienceLevelApi = new ExperienceLevelApi(...params);
export const contractsApi = new ContractsApi(...params);
export const idDocumentsApi = new EmployeeDocumentApi(...params);
export const bankingInformationApi = new BankingInformationApi(...params);
export const employeesApi = new EmployeesApi(...params);
export const departmentsEmployeesApi = new DepartmentsEmployeesApi(...params);
export const accountApi = new AccountApi(...params);
export const calendarEntriesApi = new DepartmentsEmployeesCalendarEntriesApi(...params);
export const agreementApi = new AgreementsApi(...params);
export const buildingApi = new BuildingsApi(...params);
export const roomApi = new RoomsApi(...params);
export const documentTypesApi = new DocumentTypesApi(...params);
export const statusApi = new StatusApi(...params);
export const employeeNotesApi = new EmployeeNoteApi(...params);
export const communicationsApi = new CommunicationsApi(...params);
export const employeePaymentsApi = new EmployeePaymentsApi(...params);
export const seasonApi = new SeasonsApi(...params);
export const userApi = new UsersApi(...params);
export const tokenApi = new TokenApi(...params);
// PreContractsApi is generated from the backend PreContractsController.
// After adding the backend module, run `npm run generate-api` to regenerate.
export const preContractsApi = new PreContractsApi(...params);
export const notificationApi = new NotificationsApi(...params);
export const tenantConfigApi = new TenantConfigApi(...params);
export const rewardApi = new RewardsApi(...params);
export const jobAdvertisementsApi = new JobAdvertisementsApi(...params);
export const licenceApi = new LicenceApi(...params);
export const additionalInformationsApi = new AdditionalInformationApi(...params);
export const shiftTemplateApi = new ShiftTemplateApi(...params);
export const shiftApi = new ShiftApi(...params);
export const calendarEntriesShiftTemplateApi = new CalendarEntryShiftTemplateApi(...params);
export const paymentMethodsApi = new PaymentMethodsApi(...params);
export const paymentSectionsApi = new SectionsApi(...params);
export const turnoverApi = new TurnoverItemsApi(...params);
export const depositsApi = new SimpleDepositsApi(...params);
export const tenantBankingInformationApi = new TenantBankingInformationApi(...params);
export const dailyNotesApi = new DailyNotesApi(...params);
// ShiftNotesApi is generated from the backend ShiftNotesController.
// After changing the backend module, run `npm run generate-api` to regenerate.
export const shiftNotesApi = new ShiftNotesApi(...params);
// EmployeeDayNotesApi is generated from the backend EmployeeDayNotesController.
// After changing the backend module, run `npm run generate-api` to regenerate.
export const employeeDayNotesApi = new EmployeeDayNotesApi(...params);
export const statusChangeApi = new StatusChangeApi(...params);
export const publicDocumentsApi = new PublicDocumentsApi(...params);
export const resumeApi = new ResumeApi(...params);
export const ticketApi = new DailyTicketsApi(...params);
export const safetyAreasApi = new SafetyAreasApi(...params);
export const safetyItemsApi = new SafetyItemsApi(...params);
export const pipelinesApi = new PipelinesApi(...params);
export const applicationsApi = new ApplicationsApi(...params);
export const candidateCommentsApi = new CandidateCommentsApi(...params);
export const interviewsApi = new InterviewsApi(...params);
export const emailTemplatesApi = new EmailTemplatesApi(...params);
export const offerLettersApi = new OfferLettersApi(...params);
export const courseTypesApi = new CourseTypesApi(...params);
export const trainingCoursesApi = new TrainingCoursesApi(...params);
export const trainingRequirementsApi = new TrainingRequirementsApi(...params);
export const employeeCertificationsApi = new EmployeeCertificationsApi(...params);
export const trainingComplianceApi = new TrainingComplianceApi(...params);
export const dashboardApi = new DashboardApi(...params);
export const candidatesApi = new CandidatesApi(...params);
export const timeEntriesApi = new TimeEntriesApi(...params);
export const timeEntryApprovalsApi = new TimeEntryApprovalsApi(...params);
export const reportsApi = new TimeEntryReportsApi(...params);
export const holidaysApi = new PublicHolidaysApi(...params);

// --- Absence catalog (causali) -------------------------------------------------
// Hand-written client because api.ts is generated; after the backend ships, running
// `npm run generate-api` will produce a typed AbsenceTypesApi to replace this.
export interface AbsenceTypeEntity {
  id: number;
  code: string;
  labelEn: string;
  labelIt: string;
  labelDe: string;
  labelFr: string;
  colour: string | null;
  legacyType: 'HALF_DAY' | 'DAY_OFF' | 'VACATION' | 'ILL' | 'UNPAID_LEAVE' | 'CUSTOM';
  paid: boolean;
  requiresCertificate: boolean;
  protocolType: 'NONE' | 'INPS' | 'INAIL';
  payer: 'DATORE' | 'INPS' | 'INAIL' | 'NON_RETRIBUITO';
  consumesBalance: 'NONE' | 'FERIE' | 'ROL' | 'EXFEST' | 'STUDIO' | 'HOURBANK';
  unit: 'DAY' | 'HOUR';
  countsAsWorkedHours: boolean;
  countsAsRestDay: boolean;
  monthlyCapHours: number | null;
  annualCapHours: number | null;
  legalRef: string | null;
  isSystem: boolean;
  isActive: boolean;
  sortOrder: number;
}

export const absenceTypesApi = {
  findAll: async (includeInactive = false) =>
    realAxios.get<AbsenceTypeEntity[]>('absence-types', {
      params: includeInactive ? { includeInactive: 'true' } : {},
    }),
  seedDefaults: async () =>
    realAxios.post<{ created: number; total: number }>('absence-types/seed'),
  create: async (dto: Partial<AbsenceTypeEntity>) =>
    realAxios.post<AbsenceTypeEntity>('absence-types', dto),
  update: async (id: number, dto: Partial<AbsenceTypeEntity>) =>
    realAxios.patch<AbsenceTypeEntity>(`absence-types/${id}`, dto),
  remove: async (id: number) => realAxios.delete(`absence-types/${id}`),
};

// --- Company closures (chiusura aziendale) ------------------------------------
export type ClosureType = 'CLOSURE' | 'FERIE_COLLETTIVE' | 'PONTE';
export interface CompanyClosureEntity {
  id: number;
  startDate: string;
  endDate: string;
  type: ClosureType;
  paid: boolean;
  label: string | null;
  seasonId: number | null;
  departmentId: number | null;
}

export const companyClosuresApi = {
  findAll: async (params?: { from?: string; to?: string; seasonId?: number }) =>
    realAxios.get<CompanyClosureEntity[]>('company-closures', { params }),
  create: async (dto: {
    startDate: string; endDate: string; type?: ClosureType; paid?: boolean;
    label?: string; seasonId?: number; departmentId?: number;
  }) => realAxios.post<CompanyClosureEntity>('company-closures', dto),
  update: async (id: number, dto: Partial<CompanyClosureEntity>) =>
    realAxios.patch<CompanyClosureEntity>(`company-closures/${id}`, dto),
  remove: async (id: number) => realAxios.delete(`company-closures/${id}`),
};

// --- Calendar request approvers ------------------------------------------------
// Empty list = legacy flow (managers/owners + department chiefs). Non-empty =
// only the listed employees (plus managers/owners) see and decide requests.
export interface CalendarApproverEntity {
  id: number;
  employeeId: number;
  createdAt: string;
  employee: {
    id: number;
    firstName: string;
    lastName: string;
    email: string | null;
    role: string;
  };
}

export const calendarApproversApi = {
  findAll: async () => realAxios.get<CalendarApproverEntity[]>('calendar-approvers'),
  me: async () => realAxios.get<{ configured: boolean; isApprover: boolean }>('calendar-approvers/me'),
  add: async (employeeId: number) =>
    realAxios.post<CalendarApproverEntity>('calendar-approvers', { employeeId }),
  remove: async (employeeId: number) => realAxios.delete(`calendar-approvers/${employeeId}`),
};

// --- Regulatory compliance (D.Lgs 66/2003) ------------------------------------
export type ComplianceViolationType =
  | 'DAILY_REST' | 'WEEKLY_REST' | 'DAILY_MAX' | 'WEEKLY_MAX' | 'BREAK_MISSING' | 'ABSENCE_OVERLAP';
export interface ComplianceViolation {
  employeeId: number;
  employeeName: string;
  type: ComplianceViolationType;
  severity: 'warning' | 'block';
  date: string;
  detail: string;
  meta?: Record<string, any>;
}
export const complianceApi = {
  getViolations: async (params: { from: string; to: string; seasonId?: number; departmentId?: number }) =>
    realAxios.get<{ mode: string; violations: ComplianceViolation[] }>('compliance/violations', { params }),
  checkAssignment: async (params: { employeeId: number; date: string; seasonId?: number }) =>
    realAxios.get<{ mode: string; conflicts: ComplianceViolation[] }>('compliance/check-assignment', { params }),
};

// --- Leave balances (ferie / permessi: maturato / goduto / residuo) -----------
export interface LeaveBucket {
  entitlementHours: number | null;
  carryOverHours: number;
  maturedHours: number | null;
  usedHours: number;
  residualHours: number | null;
  maturedMonths: number;
}
export interface LeaveBalances {
  year: number;
  dailyHours: number;
  ferie: LeaveBucket;
  permessi: LeaveBucket;
}
export const leaveBalancesApi = {
  getBalances: async (employeeId: number, params?: { seasonId?: number; year?: number }) =>
    realAxios.get<LeaveBalances>(`leave-balances/${employeeId}`, { params }),
};

// --- Banca ore (monte ore) ----------------------------------------------------
export interface BancaOreRow { month: string; expectedHours: number; plannedHours: number; delta: number; cumulative: number; }
export interface BancaOreLedger {
  dailyHours: number;
  rows: BancaOreRow[];
  totals: { expectedHours: number; plannedHours: number; balance: number };
}
export const bancaOreApi = {
  getLedger: async (employeeId: number, params?: { seasonId?: number; from?: string; to?: string }) =>
    realAxios.get<BancaOreLedger>(`banca-ore/${employeeId}`, { params }),
};

// --- Period report: per-employee daily detail (contractual vs planned vs worked) ---
// Hand-written because api.ts is generated; after running `npm run generate-api`
// this endpoint will appear on TimeEntryReportsApi (reports/employee-daily-detail).
export interface DailyDetailPoint {
  date: string;
  contractualTargetMinutes: number;
  plannedMinutes: number;
  workedMinutes: number;
}
export interface EmployeeDailyDetailResponse {
  employeeId: number;
  firstName: string;
  lastName: string;
  daily: DailyDetailPoint[];
  totalContractualMinutes: number;
  totalPlannedMinutes: number;
  totalWorkedMinutes: number;
}
export const reportsDailyDetailApi = {
  getEmployeeDailyDetail: async (params: {
    seasonId: number;
    employeeId: number;
    fromDate: string;
    toDate: string;
  }) => realAxios.get<EmployeeDailyDetailResponse>('reports/employee-daily-detail', { params }),
};

// --- Talent pool (Collezione candidati) ----------------------------------------
// Hand-written because api.ts is generated; after the backend ships, running
// `npm run generate-api` will produce the typed CandidatesApi replacements.
export interface TalentPoolCandidate {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  employeeId: number | null;
  converted: boolean;
  matchScore: number | null;
  bestMatchScore: number | null;
  /** Italian original. */
  matchReasoning: string | null;
  matchReasoningIt: string | null;
  matchReasoningDe: string | null;
  matchReasoningEn: string | null;
  skills: string[];
  languages: Array<{ name: string; level: string | null } | string>;
  summary: string | null;
  latestApplicationId: number | null;
  hasResumeData: boolean;
  lastAppliedAt: string | null;
  lastJobTitle: string | null;
  lastJobAdvertisementId: number | null;
  lastDepartment: { id: number; name: string } | null;
  lastSeason: { id: number; name: string } | null;
  lastStage: string | null;
  lastStageType: string | null;
  lastSource: string | null;
  lastStatus: string | null;
  applicationCount: number;
}

export interface TalentPoolResponse {
  data: TalentPoolCandidate[];
  total: number;
  page: number;
  limit: number;
}

export interface TalentPoolFacets {
  seasons: Array<{ id: number; name: string; startDate: string }>;
  departments: Array<{ id: number; name: string }>;
  jobAdvertisements: Array<{ id: number; title: string; departmentId: number | null; seasonId: number | null }>;
  languages: string[];
  skills: string[];
  sources: Array<{ value: string; count: number }>;
  statuses: Array<{ value: string; count: number }>;
}

export interface TalentPoolQuery {
  search?: string;
  minMatchScore?: number;
  skills?: string;
  languages?: string;
  seasonIds?: string;
  departmentIds?: string;
  jobAdvertisementIds?: string;
  stageTypes?: string;
  sources?: string;
  statuses?: string;
  appliedFrom?: string;
  appliedTo?: string;
  hasCv?: string;
  converted?: string;
  sort?: 'recent' | 'score' | 'name';
  page?: number;
  limit?: number;
}

export const talentPoolApi = {
  search: async (query: TalentPoolQuery) =>
    realAxios.get<TalentPoolResponse>('candidates/talent-pool', { params: query }),
  facets: async () => realAxios.get<TalentPoolFacets>('candidates/talent-pool/facets'),
};

// --- Job advertisement page analytics ------------------------------------------
export interface JobAdStatsQuery {
  seasonId?: number;
  jobAdvertisementId?: number;
  departmentId?: number;
  from?: string;
  to?: string;
  days?: number;
}

export interface JobAdBreakdownRow {
  views: number;
  share: number;
  avgDurationSeconds: number;
}

export interface JobAdStats {
  range: { from: string; to: string };
  totals: {
    views: number;
    engagedViews: number;
    bounceRate: number;
    applications: number;
    appliedViews: number;
    conversionRate: number;
    avgDurationSeconds: number;
    totalTimeSeconds: number;
    avgScrollPercent: number;
  };
  byLanguage: Array<JobAdBreakdownRow & { language: string }>;
  bySource: Array<JobAdBreakdownRow & { source: string }>;
  byDevice: Array<JobAdBreakdownRow & { deviceType: string }>;
  timeline: Array<{ date: string; views: number; applications: number }>;
  byJobAd: Array<{
    jobAdvertisementId: number;
    title: string;
    isPublic: boolean;
    departmentName: string | null;
    seasonName: string | null;
    views: number;
    applications: number;
    conversionRate: number;
    avgDurationSeconds: number;
    avgScrollPercent: number;
  }>;
}

export const jobAdAnalyticsApi = {
  getStats: async (query: JobAdStatsQuery) =>
    realAxios.get<JobAdStats>('job-ad-analytics/stats', { params: query }),
};

// --- AI match reasoning ---------------------------------------------------------
export interface MatchReasoningBackfillResult {
  processed: number;
  translated: number;
  failed: number;
  remaining: number;
}

export const matchReasoningApi = {
  /**
   * Machine-translates the Italian match explanation into German and English for
   * applications scored before those slots existed. Bounded per call — the
   * caller repeats until `remaining` reaches 0.
   */
  backfillTranslations: async (limit = 25) =>
    realAxios.post<MatchReasoningBackfillResult>(
      'resume/backfill-reasoning-translations',
      undefined,
      { params: { limit } },
    ),
};

// --- Employee newsletter ------------------------------------------------------
export * from './newsletter';
